import {
  Building,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  FileText,
  Globe,
  Info,
  Mail,
  MapPin,
  Phone,
  Printer,
  Search,
  User,
} from 'lucide-react';
import React, { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ProviderMention } from '@/services/types/treatment-types';
// Contact button for quick access
const ContactButton = ({
  icon: Icon,
  href,
  label,
}: {
  icon: React.ElementType;
  href: string;
  label: string;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center px-2 py-1 gap-1.5 text-xs rounded bg-secondary/50 hover:bg-secondary/80 transition-colors"
        aria-label={label}
      >
        <Icon className="h-3.5 w-3.5" />
        <span className="hidden sm:inline text-xs truncate">{label}</span>
      </a>
    </TooltipTrigger>
    <TooltipContent side="top" className="text-xs">
      {label}
    </TooltipContent>
  </Tooltip>
);

// Get icon for contact handle type
const getHandleIcon = (handleType: string) => {
  switch (handleType) {
    case 'PHONE':
      return Phone;
    case 'EMAIL':
      return Mail;
    case 'WEBSITE':
      return Globe;
    case 'FAX':
      return Printer;
    default:
      return Info;
  }
};

// Generate href for handles
const getHandleHref = (handleType: string, value: string) => {
  switch (handleType) {
    case 'PHONE':
      return `tel:${value}`;
    case 'EMAIL':
      return `mailto:${value}`;
    case 'WEBSITE':
      return value;
    case 'FAX':
      return `fax:${value}`;
    default:
      return '#';
  }
};

export const ProviderCard: React.FC<{ entity: ProviderMention }> = ({
  entity,
}) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Extract all the data we need
  const primary = entity.enrichment_data?.primary_hit;
  const alternatives = entity.enrichment_data?.fallback_leads || [];
  const hasAlternatives = alternatives.length > 0;
  const validEncounters =
    entity.encounters_mentioned?.filter((e) => e.date) || [];

  // Get all handles from primary hit
  const allHandles = primary?.handles || [];
  const primaryPhone = allHandles.find((h: any) => h.handle_type === 'PHONE');

  // Format names
  const displayName =
    entity.practitioner_name ||
    entity.organization_name ||
    primary?.organization_name ||
    'Unknown';

  // Check for content in different sections
  const hasExcerpts = entity.excerpts && entity.excerpts.length > 0;
  const hasAnalysis =
    !!entity.reasoning ||
    !!entity.enrichment_data?.query ||
    !!entity.enrichment_data?.summary_of_enrichment;

  // Function to copy text to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Toggle section expansion
  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  return (
    <Card className="overflow-hidden border border-border bg-card">
      {/* Minimal header - just name and confidence */}
      <div className="p-3 flex items-center justify-between bg-muted/10 border-b border-border">
        <div className="flex items-center gap-1.5">
          <h3
            className="text-sm font-medium truncate flex-1"
            title={displayName}
          >
            {displayName}
          </h3>
          {entity.confidence >= 0.8 && (
            <Badge
              variant="outline"
              className="bg-green-50/50 text-green-700 border-0 px-1 py-0 h-4 text-[10px]"
            >
              {Math.round(entity.confidence * 100)}%
            </Badge>
          )}
        </div>

        {/* Primary phone - most important piece of contact info */}
        {primaryPhone && (
          <a
            href={`tel:${primaryPhone.value}`}
            className="ml-auto text-xs flex items-center gap-1 px-2 py-1 bg-secondary/30 rounded"
          >
            <Phone className="h-3.5 w-3.5" />
            <span className="hidden sm:inline truncate max-w-[120px]">
              {primaryPhone.value}
            </span>
          </a>
        )}
      </div>

      {/* Progressive disclosure sections */}
      <div className="text-xs">
        {/* Contact Info Section */}
        <button
          type="button"
          onClick={() => toggleSection('contact')}
          className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/5 border-b border-border/50"
        >
          <div className="flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">Contact Information</span>
          </div>
          {expandedSection === 'contact' ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </button>

        {expandedSection === 'contact' && (
          <div className="p-3 border-b border-border/50 bg-muted/5 space-y-4">
            {/* Address */}
            {primary?.address && (
              <div className="flex items-start gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="flex-1 flex items-center gap-1">
                  <span className="text-muted-foreground">
                    {primary.address}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(primary.address || '')}
                    className="p-1 hover:bg-muted rounded"
                  >
                    <Copy className="h-3 w-3 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}

            {/* All contact methods */}
            {allHandles.length > 0 && (
              <div className="space-y-2">
                <div className="text-[10px] uppercase text-muted-foreground">
                  Contact Methods
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {allHandles.map((handle: any, i) => {
                    const Icon = getHandleIcon(handle.handle_type);
                    const href = getHandleHref(
                      handle.handle_type,
                      handle.value,
                    );

                    return (
                      <div key={i} className="flex items-center">
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 flex-1"
                        >
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="truncate">{handle.value}</span>
                          {handle.name && (
                            <Badge
                              variant="outline"
                              className="ml-1 px-1 py-0 h-4 text-[10px]"
                            >
                              {handle.name}
                            </Badge>
                          )}
                        </a>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(handle.value)}
                          className="p-1 hover:bg-muted rounded ml-1.5"
                        >
                          <Copy className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Specialty */}
            {entity.specialty_hint && (
              <div className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {entity.specialty_hint}
                </span>
              </div>
            )}

            {/* Encounters */}
            {validEncounters.length > 0 && (
              <div className="space-y-2">
                <div className="text-[10px] uppercase text-muted-foreground">
                  Encounters
                </div>
                <div className="flex flex-wrap gap-1">
                  {validEncounters.map((encounter, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="bg-amber-50/50 text-amber-700 border-0 flex items-center gap-1 py-0 h-5 px-1.5"
                    >
                      <Clock className="h-2.5 w-2.5" />
                      <span className="text-[10px]">{encounter.date}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Alternatives Section */}
        {hasAlternatives && (
          <>
            <button
              type="button"
              onClick={() => toggleSection('alternatives')}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/5 border-b border-border/50"
            >
              <div className="flex items-center gap-1.5">
                <Building className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">
                  Alternative Providers ({alternatives.length})
                </span>
              </div>
              {expandedSection === 'alternatives' ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </button>

            {expandedSection === 'alternatives' && (
              <div className="p-3 border-b border-border/50 bg-muted/5 space-y-3">
                {alternatives.map((alt, i) => (
                  <div key={i} className="p-2 bg-muted/30 rounded space-y-1.5">
                    {/* Name */}
                    <div className="flex items-center gap-1.5">
                      {alt.organization_name ? (
                        <Building className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      <span className="font-medium">
                        {alt.organization_name || alt.practitioner_name}
                      </span>
                    </div>

                    {/* Address */}
                    {alt.address && (
                      <div className="flex items-start gap-1.5 text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
                        <span>{alt.address}</span>
                      </div>
                    )}

                    {/* Contact buttons */}
                    {alt.handles?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {alt.handles.map((h: any, j) => {
                          const Icon = getHandleIcon(h.handle_type);
                          const href = getHandleHref(h.handle_type, h.value);

                          return (
                            <a
                              key={j}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 bg-secondary/50 rounded"
                            >
                              <Icon className="h-3 w-3" />
                              <span>{h.value}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Excerpts Section */}
        {hasExcerpts && (
          <>
            <button
              type="button"
              onClick={() => toggleSection('excerpts')}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/5 border-b border-border/50"
            >
              <div className="flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">
                  Document Excerpts ({entity.excerpts?.length})
                </span>
              </div>
              {expandedSection === 'excerpts' ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </button>

            {expandedSection === 'excerpts' && (
              <div className="p-3 border-b border-border/50 bg-muted/5">
                <div className="grid gap-2">
                  {entity.excerpts?.map((excerpt, i) => (
                    <div
                      key={i}
                      className="p-2 rounded-md bg-secondary/30 italic"
                    >
                      {excerpt}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Analysis Section */}
        {hasAnalysis && (
          <>
            <button
              type="button"
              onClick={() => toggleSection('analysis')}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/5 border-b border-border/50"
            >
              <div className="flex items-center gap-1.5">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">Analysis & Pipeline</span>
              </div>
              {expandedSection === 'analysis' ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </button>

            {expandedSection === 'analysis' && (
              <div className="p-3 border-b border-border/50 bg-muted/5 space-y-2">
                {/* Reasoning */}
                {entity.reasoning && (
                  <div className="p-2 rounded-md bg-blue-50/30 border border-blue-100">
                    <div className="uppercase text-[10px] text-blue-700 mb-1">
                      Analysis
                    </div>
                    <div>{entity.reasoning}</div>
                  </div>
                )}

                {/* Query */}
                {entity.enrichment_data?.query && (
                  <div className="p-2 rounded-md bg-purple-50/30 border border-purple-100">
                    <div className="uppercase text-[10px] text-purple-700 mb-1 flex items-center gap-1">
                      <Search className="h-3 w-3" />
                      Search query
                    </div>
                    <div className="italic">{entity.enrichment_data.query}</div>
                  </div>
                )}

                {/* Results */}
                {entity.enrichment_data?.summary_of_enrichment && (
                  <div className="p-2 rounded-md bg-green-50/30 border border-green-100">
                    <div className="uppercase text-[10px] text-green-700 mb-1">
                      Results found
                    </div>
                    <div>{entity.enrichment_data.summary_of_enrichment}</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};
